import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "react-i18next";

interface PromptTabProps {
  formData: any;
  onFormChange: (field: string, value: any) => void;
}

const PromptTab: React.FC<PromptTabProps> = ({ formData, onFormChange }) => {
  const { t } = useTranslation("assistants");
  const selectedMode = formData.prompt_mode === 'default-prompt' ? 'default' : 'guided';

  const handleModeChange = (mode: string) => {
    const promptMode = mode === 'default' ? 'default-prompt' : 'guided-mode';
    onFormChange('prompt_mode', promptMode);
  };

  const buildDefaultPromptFromGuided = () => {
    const sections = [];

    if (formData.voice_tone?.trim()) {
      sections.push(`#Tom de voz\n${formData.voice_tone.trim()}`);
    }

    if (formData.main_objective?.trim()) {
      sections.push(`#Objetivo Principal\n${formData.main_objective.trim()}`);
    }

    if (formData.language_rules?.trim()) {
      sections.push(`#Regras de linguagem\n${formData.language_rules.trim()}`);
    }

    if (formData.good_examples?.trim()) {
      sections.push(`#Exemplos de boas respostas\n${formData.good_examples.trim()}`);
    }

    if (formData.restrictions?.trim()) {
      sections.push(`#Restrições\n${formData.restrictions.trim()}`);
    }

    if (formData.additional_info?.trim()) {
      sections.push(`#Informações adicionais\n${formData.additional_info.trim()}`);
    }

    return sections.join('\n\n');
  };

  const handleGuidedFieldChange = (field: string, value: string) => {
    onFormChange(field, value);

    const updatedFormData = { ...formData, [field]: value };

    const sections = [];

    if (updatedFormData.voice_tone?.trim()) {
      sections.push(`#Tom de voz\n${updatedFormData.voice_tone.trim()}`);
    }

    if (updatedFormData.main_objective?.trim()) {
      sections.push(`#Objetivo Principal\n${updatedFormData.main_objective.trim()}`);
    }

    if (updatedFormData.language_rules?.trim()) {
      sections.push(`#Regras de linguagem\n${updatedFormData.language_rules.trim()}`);
    }

    if (updatedFormData.good_examples?.trim()) {
      sections.push(`#Exemplos de boas respostas\n${updatedFormData.good_examples.trim()}`);
    }

    if (updatedFormData.restrictions?.trim()) {
      sections.push(`#Restrições\n${updatedFormData.restrictions.trim()}`);
    }

    if (updatedFormData.additional_info?.trim()) {
      sections.push(`#Informações adicionais\n${updatedFormData.additional_info.trim()}`);
    }

    const defaultPrompt = sections.join('\n\n');
    onFormChange('default_prompt', defaultPrompt);
  };

  return (
    <form
      action=""
      className="w-full flex justify-center items-center flex-col gap-4 p-1 md:p-2"
    >
      <RadioGroup value={selectedMode} onValueChange={handleModeChange}>
        <div className="flex items-center gap-6">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="guided" id="guided" />
            <Label htmlFor="guided" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("prompt.guided")}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="default" id="default" />
            <Label htmlFor="default" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("prompt.default")}</Label>
          </div>
        </div>
      </RadioGroup>
      {selectedMode === "guided" ? (
        <div className="w-full grid pb-8 mt-3 grid-cols-2 gap-4">
          {/* voice tom ---> */}
          <div className="flex w-full flex-col items-start gap-3">
            <Label htmlFor="voice-tom" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("prompt.voiceTone")}</Label>
            <Textarea
              placeholder={t("prompt.voiceTonePlaceholder")}
              value={formData.voice_tone || ''}
              onChange={(e) => handleGuidedFieldChange('voice_tone', e.target.value)}
              className="!h-[120px]"
            />
          </div>
          {/* Main objective ---> */}
          <div className="flex w-full flex-col items-start gap-3">
            <Label htmlFor="main-objective" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("prompt.mainObjective")}</Label>
            <Textarea
              placeholder={t("prompt.mainObjectivePlaceholder")}
              value={formData.main_objective || ''}
              onChange={(e) => handleGuidedFieldChange('main_objective', e.target.value)}
              className="!h-[120px]"
            />
          </div>
          {/* Language rules ---> */}
          <div className="flex w-full flex-col items-start gap-3">
            <Label htmlFor="language-rules" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("prompt.languageRules")}</Label>
            <Textarea
              placeholder={t("prompt.languageRulesPlaceholder")}
              value={formData.language_rules || ''}
              onChange={(e) => handleGuidedFieldChange('language_rules', e.target.value)}
              className="!h-[120px]"
            />
          </div>
          {/* Examples of good answers ---> */}
          <div className="flex w-full flex-col items-start gap-3">
            <Label htmlFor="examples-good-answers" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">
              {t("prompt.goodExamples")}
            </Label>
            <Textarea
              placeholder={t("prompt.goodExamplesPlaceholder")}
              value={formData.good_examples || ''}
              onChange={(e) => handleGuidedFieldChange('good_examples', e.target.value)}
              className="!h-[120px]"
            />
          </div>
          {/* Restrictions ---> */}
          <div className="flex w-full flex-col items-start gap-3">
            <Label htmlFor="restrictions" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("prompt.restrictions")}</Label>
            <Textarea
              placeholder={t("prompt.restrictionsPlaceholder")}
              value={formData.restrictions || ''}
              onChange={(e) => handleGuidedFieldChange('restrictions', e.target.value)}
              className="!h-[120px]"
            />
          </div>
          {/* Additional information ---> */}
          <div className="flex col-span-2 w-full flex-col items-start gap-3">
            <Label htmlFor="additional-information" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">
              {t("prompt.additionalInfo")}
            </Label>
            <Textarea
              placeholder={t("prompt.restrictionsPlaceholderAlt")}
              value={formData.additional_info || ''}
              onChange={(e) => handleGuidedFieldChange('additional_info', e.target.value)}
              className="!h-[120px]"
            />
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-3">
          <Label htmlFor="voice-tom" className="font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("prompt.additionalInfo")} </Label>
          <Textarea
            placeholder={t("prompt.additionalInfoPlaceholder")}
            value={formData.default_prompt || ''}
            onChange={(e) => onFormChange('default_prompt', e.target.value)}
            className="!h-[560px]"
          />
        </div>
      )}
    </form>
  );
};

export default PromptTab;
